import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { CalendarCheck, Sparkles } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const baseInitial = {
  name: "",
  email: "",
  phone: "",
  project_type: "",
  budget: "",
  preferred_date: "",
  message: "",
};

export const Consultation = () => {
  const { t, lang } = useLanguage();
  const projectTypes = t("consultation.projectTypes");
  const budgets = t("consultation.budgets");
  const benefits = t("consultation.benefits");

  const [form, setForm] = useState({
    ...baseInitial,
    project_type: projectTypes[0],
    budget: budgets[0],
  });
  const [loading, setLoading] = useState(false);

  // Keep select values consistent with the active language
  useEffect(() => {
    setForm((f) => ({ ...f, project_type: projectTypes[0], budget: budgets[0] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/consultation`, form);
      toast.success(t("consultation.success"));
      setForm({ ...baseInitial, project_type: projectTypes[0], budget: budgets[0] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("consultation.error"));
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] text-white py-3 px-0 outline-none transition-colors duration-300 placeholder:text-[#A0ABC0]/60";
  const selectClass =
    "w-full bg-[#0B1622] border-b border-white/20 focus:border-[#D4AF37] text-white py-3 px-0 outline-none transition-colors duration-300";

  return (
    <section
      id="consultation"
      data-testid="consultation-section"
      className="relative py-28 sm:py-40 bg-[#0A1420]"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="lg:col-span-5"
          >
            <p className="text-[#D4AF37] tracking-[0.3em] uppercase text-xs mb-6 flex items-center gap-2">
              <Sparkles size={15} /> {t("consultation.eyebrow")}
            </p>
            <h2 className="font-serif-lux text-4xl sm:text-5xl lg:text-6xl text-white leading-tight">
              {t("consultation.title")} <span className="gold-gradient-text italic">{t("consultation.titleAccent")}</span>
            </h2>
            <p className="mt-8 text-[#A0ABC0] font-light leading-relaxed max-w-md">
              {t("consultation.subtitle")}
            </p>

            <ul className="mt-10 space-y-5">
              {benefits.map((b) => (
                <li key={b} className="flex items-start gap-4 text-white/90" data-testid="consultation-benefit">
                  <CalendarCheck className="text-[#D4AF37] mt-0.5 shrink-0" size={20} />
                  <span className="font-light">{b}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.15 }}
            data-testid="consultation-form"
            className="lg:col-span-7 bg-[#0B1622] border border-[#D4AF37]/15 p-8 sm:p-10 space-y-7"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-7">
              <input
                required
                value={form.name}
                onChange={update("name")}
                placeholder={t("consultation.name")}
                data-testid="consultation-name-input"
                className={inputClass}
              />
              <input
                required
                type="email"
                value={form.email}
                onChange={update("email")}
                placeholder={t("consultation.email")}
                data-testid="consultation-email-input"
                className={inputClass}
              />
              <input
                value={form.phone}
                onChange={update("phone")}
                placeholder={t("consultation.phone")}
                data-testid="consultation-phone-input"
                className={inputClass}
              />
              <input
                value={form.preferred_date}
                onChange={update("preferred_date")}
                placeholder={t("consultation.date")}
                data-testid="consultation-date-input"
                className={inputClass}
              />
              <div>
                <label className="text-xs uppercase tracking-widest text-[#A0ABC0]">{t("consultation.projectTypeLabel")}</label>
                <select
                  value={form.project_type}
                  onChange={update("project_type")}
                  data-testid="consultation-projecttype-select"
                  className={selectClass}
                >
                  {projectTypes.map((pt) => (
                    <option key={pt} value={pt}>{pt}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-[#A0ABC0]">{t("consultation.budgetLabel")}</label>
                <select
                  value={form.budget}
                  onChange={update("budget")}
                  data-testid="consultation-budget-select"
                  className={selectClass}
                >
                  {budgets.map((b) => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </select>
              </div>
            </div>
            <textarea
              rows={4}
              value={form.message}
              onChange={update("message")}
              placeholder={t("consultation.messagePlaceholder")}
              data-testid="consultation-message-input"
              className={`${inputClass} resize-none`}
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="consultation-submit-button"
              className="w-full sm:w-auto bg-[#D4AF37] text-[#0A1420] font-semibold hover:bg-[#E6C27A] transition-colors duration-300 px-10 py-4 uppercase tracking-widest text-sm disabled:opacity-60"
            >
              {loading ? t("consultation.submitting") : t("consultation.submit")}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};
